import { makeStyles } from '@mui/styles';
import { neutral3, primary } from 'apollo-react/colors';
import Panel from 'apollo-react/components/Panel';
import classNames from 'classnames';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import { ResizableBox } from 'react-resizable';

import ConfirmModal from './../../form-designer/components/editor/components/ConfirmModal';
import { clearWarnings } from '../../actions/WarningActions';
import { onGoingCalls, supportDataCache } from '../../api/get-support-data';
import CustomLoader from '../../components/CustomLoader';
import EditorErrorPage from '../../components/EditorErrorPage';
import Layout from '../../components/Layout';
import PanelWrapper from '../../components/PanelWrapper';
import { FORMS_LIST } from '../../constants/Urls';
import { useFormSelector } from '../../custom-hooks';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { loadEditor, toggleEditMode } from '../../form-designer/actions/editor.actions';
import { clearFormDesigner } from '../../form-designer/actions/form-manager.actions';
import ControlProperties from '../../form-designer/components/editor/ControlProperties';
import EditorHeader from '../../form-designer/components/editor/EditorHeader';
import RulesList from '../../form-designer/components/editor/RulesList';
import {
  BOTTOM_PANEL_COEFFICIENT,
  FORMS_DESIGN_RIGHT,
  LAYOUT_HEADER_OFFSET,
  TOP_PANEL_COEFFICIENT,
  VERTICAL_HANDLER_OFFSET,
} from '../../form-designer/constants/editor.constants';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import FormManager from '../../form-designer/FormManager';
import {
  getIsDockToBottom,
  getUserRights,
  hasFetchError,
  isFormLoading,
} from '../../form-designer/selectors/common.selectors';
import { clearCache } from '../../utils/cacheUtils';

const useStyles = makeStyles({
  container: {
    position: 'relative',
  },
  wrapperStyled: {
    display: 'flex',
    height: 'calc(100vh - 66px)',
    overflow: 'hidden',
  },
  formWrapperStyled: {
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  editorFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 100%',
    minWidth: 0,
    height: '97vh',
    overflow: 'hidden',
  },
  panelGroup: {
    display: 'flex',
    padding: 0,
    overflow: 'hidden',
  },
  loader: {
    height: '100vh',
    overflow: 'hidden',
  },
  middlePanel: {
    paddingLeft: 5,
  },
  rightPanel: {
    padding: '20px 20px 0 20px',
    height: 'calc(100vh - 157px)',
    overflow: 'auto',
  },
  rulesGrid: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'auto',
  },
  rulesContainer: {
    borderTop: 'solid 1px #eee',
  },
  handleContainer: {
    zIndex: 10,
    position: 'absolute',
    width: '100%',
    cursor: 'ns-resize',
    backgroundColor: 'transparent',
    '&:hover div': {
      backgroundColor: primary,
      width: '100%',
      height: 3,
    },
  },
  handle: {
    position: 'absolute',
    right: 0,
    height: 1,
    width: '100%',
    backgroundColor: neutral3,
  },
});

function FormEditorPage({ location }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isLoading = useFormSelector(isFormLoading(FORM_KEYS.EDITOR));
  const userRights = useSelector(getUserRights);
  const isFetchError = useFormSelector(hasFetchError);
  const [mainPanelHeight, setMainPanelHeight] = useState();
  const [rulePanelHeight, setRulePanelHeight] = useState();
  const isDockToBottom = useSelector(getIsDockToBottom);
  const { height } = useWindowDimensions();

  useEffect(() => {
    const qsParams = qs.parse(location.search) || {};

    const { isCreateForm, isImport: isImportMode } = qsParams;

    if (isImportMode) {
      return;
    }

    const newFormParams = {
      orgCode: '',
      formKey: qsParams.formKey,
      formEntity: qsParams.formEntity,
      formStyle: 'ClientEnableForm',
      formSubEntity: qsParams.formSubEntity,
    };

    if (userRights.includes(FORMS_DESIGN_RIGHT)) {
      dispatch(
        loadEditor({
          entityViewName: qsParams.entityViewName,
          entityType: qsParams.entityType,
          containerWorkflowKey: qsParams.containerWorkflowKey,
          workflowKey: qsParams.workflowKey,
          stepId: qsParams.stepId,
          itmId: qsParams.itmId,
          actionName: qsParams.actionName,
          workflowId: qsParams.workflowId,
          parentWorkflowId: qsParams.parentWorkflowId,
          formKey: qsParams.formKey,
          formId: qsParams.formId,
          entityName: qsParams.entityName,
          isCreateForm,
          newFormParams,
        })
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, userRights]);

  useEffect(() => {
    return () => {
      clearCache(supportDataCache);
      clearCache(onGoingCalls);
      dispatch(clearWarnings());
      dispatch(toggleEditMode(false));
      dispatch(clearFormDesigner(FORM_KEYS.EDITOR));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    setMainPanelHeight(document.body.clientHeight - LAYOUT_HEADER_OFFSET);
    setRulePanelHeight(
      (document.body.clientHeight - LAYOUT_HEADER_OFFSET) * BOTTOM_PANEL_COEFFICIENT
    );
  }, [height]);

  const onResize = (e, data) => {
    setRulePanelHeight(mainPanelHeight - data.size.height);
  };

  return (
    <Layout dashboardLink={FORMS_LIST}>
      <DndProvider backend={HTML5Backend}>
        <FormContext.Provider value={FORM_KEYS.EDITOR}>
          <div className={classes.wrapperStyled}>
            <div className={classes.formWrapperStyled} ui-ta="ScrollingElement">
              {isFetchError ? (
                <EditorErrorPage />
              ) : isLoading ? (
                <div className={classes.loader}>
                  <CustomLoader />
                </div>
              ) : (
                <div className={classes.editorFormContainer}>
                  <div>
                    <EditorHeader />
                  </div>
                  <PanelWrapper classes={classes}>
                    <Panel width="70%" className={classes.middlePanel} resizable hideButton>
                      {mainPanelHeight && (
                        <>
                          <ResizableBox
                            className={classes.container}
                            height={
                              isDockToBottom
                                ? mainPanelHeight * TOP_PANEL_COEFFICIENT
                                : mainPanelHeight
                            }
                            minConstraints={[Infinity, 0]}
                            maxConstraints={[Infinity, mainPanelHeight - VERTICAL_HANDLER_OFFSET]}
                            axis="y"
                            resizeHandles={['se']}
                            handle={
                              <div className={classes.handleContainer}>
                                <div className={classes.handle} />
                              </div>
                            }
                            onResize={onResize}
                          >
                            <div className={classes.rulesGrid} id="EditorCanvas">
                              <FormManager />
                            </div>
                          </ResizableBox>
                          <div
                            className={classNames(classes.rulesContainer)}
                            id="RuleSectionCanvas"
                          >
                            <RulesList
                              height={rulePanelHeight ?? mainPanelHeight * BOTTOM_PANEL_COEFFICIENT}
                            />
                          </div>
                        </>
                      )}
                    </Panel>
                    <Panel width="100%" className={classes.rightPanel} hideButton>
                      <ControlProperties />
                    </Panel>
                  </PanelWrapper>
                </div>
              )}
            </div>
          </div>
          <ConfirmModal />
        </FormContext.Provider>
      </DndProvider>
    </Layout>
  );
}

export default FormEditorPage;
